// dashboard/components/Support.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore,
  Help,
  Email,
  Phone,
  Chat,
  Send,
  Message,
  CheckCircle,
  Support as SupportIcon,
  LiveHelp,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const SupportCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
  },
}));

const Support = () => {
  const [message, setMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const faqs = [
    {
      question: 'How do I get started with trading?',
      answer: 'We recommend starting with our Beginner\'s Course which covers all the basics of forex and crypto trading. You can access it from the "My Courses" section.',
    },
    {
      question: 'What are the trading hours?',
      answer: 'Forex markets are open 24/5 from Monday to Friday. Crypto markets are open 24/7.',
    },
    {
      question: 'How do I withdraw my funds?',
      answer: 'You can request a withdrawal from the Wallet section. Withdrawals are processed within 24-48 hours.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept Credit/Debit Cards, PayPal, Bank Transfer, and various cryptocurrencies.',
    },
    {
      question: 'How do I contact support?',
      answer: 'You can contact us via email at support@protradesinsight.com or through the contact form below.',
    },
  ];

  const handleSubmit = () => {
    if (!message.trim()) {
      setSnackbarMessage('Please enter your message.');
      setOpenSnackbar(true);
      return;
    }
    setSnackbarMessage('Your message has been sent! We\'ll get back to you soon.');
    setOpenSnackbar(true);
    setMessage('');
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#0a1628' }}>
          Support Center
        </Typography>
      </Box>

      {/* Quick Support Options */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <SupportCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'rgba(46, 204, 113, 0.1)', color: '#2ecc71', width: 56, height: 56, margin: '0 auto 16px' }}>
                <Email sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0a1628' }}>
                Email Support
              </Typography>
              <Typography variant="body2" sx={{ color: '#95a5a6', mb: 1 }}>
                support@protradesinsight.com
              </Typography>
              <Typography variant="caption" sx={{ color: '#95a5a6' }}>
                Response within 24 hours
              </Typography>
            </CardContent>
          </SupportCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SupportCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'rgba(52, 152, 219, 0.1)', color: '#3498db', width: 56, height: 56, margin: '0 auto 16px' }}>
                <Chat sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0a1628' }}>
                Live Chat
              </Typography>
              <Typography variant="body2" sx={{ color: '#95a5a6', mb: 1 }}>
                Available 24/7
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{ bgcolor: '#3498db', '&:hover': { bgcolor: '#2980b9' } }}
                startIcon={<Message />}
              >
                Start Chat
              </Button>
            </CardContent>
          </SupportCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SupportCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'rgba(155, 89, 182, 0.1)', color: '#9b59b6', width: 56, height: 56, margin: '0 auto 16px' }}>
                <Phone sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0a1628' }}>
                Phone Support
              </Typography>
              <Typography variant="body2" sx={{ color: '#95a5a6', mb: 1 }}>
                +1 (800) 123-4567
              </Typography>
              <Typography variant="caption" sx={{ color: '#95a5a6' }}>
                Mon-Fri 9AM-5PM EST
              </Typography>
            </CardContent>
          </SupportCard>
        </Grid>
      </Grid>

      {/* Contact Form */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #eef2f7', mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0a1628', mb: 2 }}>
          Send us a Message
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Your Name"
              placeholder="John Doe"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Your Email"
              placeholder="john@example.com"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subject"
              placeholder="What is your inquiry about?"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Message"
              placeholder="Describe your issue or question..."
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={handleSubmit}
              sx={{ bgcolor: '#0a1628', '&:hover': { bgcolor: '#1a2a4a' }, borderRadius: 2 }}
            >
              Send Message
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* FAQ Section */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #eef2f7' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LiveHelp sx={{ color: '#2ecc71' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0a1628' }}>
            Frequently Asked Questions
          </Typography>
        </Box>
        {faqs.map((faq, index) => (
          <Accordion 
            key={index} 
            elevation={0} 
            sx={{ 
              border: 'none', 
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.02)',
                },
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#0a1628' }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ color: '#4a4a5a', pl: 2 }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Support;